import { usePortfolioDocumentsQuery } from "web_myplann/Api/Portfolio/Queries/usePortfolioDocumentsQuery";
import { usePortfolioIdReportingQuery } from "web_myplann/Api/Portfolio/Queries/usePortfolioIdReportingQuery";
import type { PensionProductsDetailDataDTO } from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { DataLineChart } from "web_myplann/Portfolio/Components/DataLineChart/DataLineChart";
import { DetailSectionLoader } from "web_myplann/Portfolio/Components/DetailSectionLoader";
import { BasicInfo } from "web_myplann/Portfolio/Components/PortfolioSections/BasicInfo";
import { RelatedSection } from "web_myplann/Portfolio/Components/RelatedSection";
import { SectionDivider } from "web_myplann/Portfolio/Components/SectionDidiver";
import { calculateGraphData } from "web_myplann/Portfolio/Helpers/calculateGraphData";
import { computeRelatedPresence } from "web_myplann/Portfolio/Helpers/getHasRelated";
import {
	getPortfolioFrequencyValue,
	getPortfolioValueDate,
} from "web_myplann/Portfolio/Helpers/utils";
import {
	DataCard,
	DataCardPreset,
} from "web_myplann/Shared/Components/DataCard";
import { DataRow } from "web_myplann/Shared/Components/DataRow";
import { moneyFormatter } from "web_myplann/Shared/Utils/formatters";
import { EMPTY_DETAIL_FIELD } from "web_myplann/Utils/constants";
import type { PortfolioDetailProps } from "web_myplann/Utils/types";

const PageResources = Resources.PortfolioDetail;

export const PensionProduct = ({
	baseData,
	currencySign,
}: PortfolioDetailProps) => {
	const { t } = useResource();

	const { data: documents, isLoading: isDocumentsLoading } =
		usePortfolioDocumentsQuery(baseData.id);
	const { data: reporting, isLoading: isReportingLoading } =
		usePortfolioIdReportingQuery(baseData.id);

	if (isDocumentsLoading || isReportingLoading) {
		return <DetailSectionLoader />;
	}

	const { daysData, totalValue, totalInvestedValue, toDate, showChartInfo } =
		calculateGraphData(
			reporting?.reportingData,
			reporting?.summary ?? undefined,
		);

	const portfolioData = baseData.data as PensionProductsDetailDataDTO;

	const participantContribution = portfolioData.payment
		? getPortfolioFrequencyValue(
				portfolioData.payment ?? null,
				currencySign,
				portfolioData.paymentFrequency ?? null,
				t,
			)
		: EMPTY_DETAIL_FIELD;

	const employerContribution = portfolioData.employerContribution
		? getPortfolioFrequencyValue(
				portfolioData.employerContribution ?? null,
				currencySign,
				portfolioData.paymentFrequency ?? null,
				t,
			)
		: EMPTY_DETAIL_FIELD;

	const toAge = portfolioData.ageLimit
		? t(PageResources.SharedLabels.untilAge, {
				count: portfolioData.ageLimit,
			})
		: EMPTY_DETAIL_FIELD;

	return (
		<>
			<DataLineChart
				hideDivider
				totalValue={totalValue}
				totalInvestedValue={totalInvestedValue}
				totalEvaluationPercent={reporting?.summary?.totalEvaluationPercent ?? 0}
				daysData={daysData}
				currencySign={currencySign}
				toDate={toDate}
				showChartInfo
			/>

			{showChartInfo && (
				<>
					<DataRow
						label={t(Resources.Portfolio.Chart.InvestedTogether)}
						value={moneyFormatter(totalInvestedValue, currencySign, "", 2)}
					/>

					<DataRow
						label={t(Resources.Common.ToDate)}
						value={getPortfolioValueDate(daysData, baseData)}
					/>
				</>
			)}

			{daysData.length !== 0 && <SectionDivider />}

			<DataCard
				preset={DataCardPreset.BasicInfo}
				bottomComponent={<BasicInfo data={baseData} />}
				noDivider={
					!computeRelatedPresence(documents?.data, baseData.related).hasAny
				}
			>
				<DataRow
					label={t(PageResources.SharedLabels.name)}
					value={baseData.clientName}
				/>
				<DataRow
					label={t(PageResources.SharedLabels.contractNumber)}
					value={baseData.contractNumber}
				/>
				<DataRow
					label={t(PageResources.SharedLabels.producent)}
					value={baseData.producer}
				/>
				<DataRow
					label={t(PageResources.SharedLabels.product)}
					value={baseData.product}
				/>
				<DataRow
					label={t(PageResources.PensionProduct.participantContribution)}
					value={participantContribution}
				/>
				<DataRow
					label={t(PageResources.PensionProduct.employerContribution)}
					value={employerContribution}
				/>
				<DataRow label={t(PageResources.PensionProduct.toAge)} value={toAge} />
			</DataCard>

			<RelatedSection
				documents={documents?.data}
				related={baseData.related}
				portfolioId={baseData.id}
			/>
		</>
	);
};
